import type {
  ColumnState,
  FilterChangedEvent,
  GridApi
} from "ag-grid-enterprise";
import { readStoredRecord } from "../utils/readStoredRecord";

/** One column of a stored grid view. Only the choices a user made are set. */
interface GridViewColumn {
  /** The column id. */
  col_id: string;
  /** Set when the user hid the column. */
  hide?: true;
  /** Set when the column is pinned. */
  pinned?: "left" | "right";
  /** Set when the grid sorts by the column. */
  sort?: "asc" | "desc";
  /** The column's place in a multi-column sort. */
  sort_index?: number;
}

/** A grid's view: its columns in order, and its filter model. */
export interface GridViewSnapshot {
  /** Every column, in the grid's column order (pinning is per column). */
  columns: GridViewColumn[];
  /** AG Grid's filter model: column id → that column's filter. */
  filter_model: Record<string, unknown>;
}

/** What a restore could not set, because the grid does not offer it now. */
export interface GridViewRestoreResult {
  /** Stored columns that the grid does not have. */
  missingColumns: string[];
  /** Filtered columns that the grid does not have, or that have no filter. */
  missingFilters: string[];
}

/** What changed on a grid, as {@link FeedbackGridView.subscribe} reports it. */
export type GridViewChange = "columns" | "load" | "user";

/**
 * The handle `DataTable` gives the feedback integration for a grid with a
 * `feedbackViewKey`: capture, restore and change subscription.
 */
export interface FeedbackGridView {
  /** The grid's view now; null after the grid is destroyed. */
  capture: () => GridViewSnapshot | null;
  /** Sets a stored view (untrusted JSON) on the grid. */
  restore: (stored: unknown) => GridViewRestoreResult;
  /**
   * Whether the grid holds rows: any row model, filtered out or not, because
   * a restored filter can hide them all.
   */
  hasRows: () => boolean;
  /**
   * Calls `listener` with "columns" when the grid loads new column
   * definitions, with "load" when its row model updates, and with "user"
   * when the user changes the grid's columns, sort or filters. Returns the
   * unsubscribe function.
   */
  subscribe: (listener: (change: GridViewChange) => void) => () => void;
}

/** The event sources of a column change the user made in the grid's UI. */
const USER_COLUMN_SOURCES = new Set<string>([
  "toolPanelUi",
  "toolPanelDragAndDrop",
  "uiColumnMoved",
  "uiColumnDragged",
  "uiColumnSorted",
  "columnMenu",
  "contextMenu"
]);

/**
 * The event sources of a filter change the user made in the grid's UI. Not
 * "quickFilter": AG Grid fires it for any change to `quickFilterText`, also a
 * change the page's own code makes.
 */
const USER_FILTER_SOURCES = new Set<string>(["columnFilter", "advancedFilter"]);

function captureColumn(column: ColumnState): GridViewColumn {
  const captured: GridViewColumn = { col_id: column.colId };
  if (column.hide) captured.hide = true;
  if (column.pinned === "left" || column.pinned === true) {
    captured.pinned = "left";
  }
  if (column.pinned === "right") captured.pinned = "right";
  if (column.sort === "asc" || column.sort === "desc") {
    captured.sort = column.sort;
    if (typeof column.sortIndex === "number") {
      captured.sort_index = column.sortIndex;
    }
  }
  return captured;
}

/** The grid's columns (order, hidden, pinned, sort) and filters. */
export function captureGridView(api: GridApi): GridViewSnapshot | null {
  if (api.isDestroyed()) return null;
  return {
    columns: api.getColumnState().map(captureColumn),
    filter_model: api.getFilterModel() ?? {}
  };
}

/** A stored column, or null when the entry is misshapen. */
function readColumn(value: unknown): ColumnState | null {
  const column = readStoredRecord(value);
  if (!column || typeof column.col_id !== "string") return null;
  const sort =
    column.sort === "asc" || column.sort === "desc" ? column.sort : null;
  return {
    colId: column.col_id,
    hide: column.hide === true,
    pinned:
      column.pinned === "left" || column.pinned === "right"
        ? column.pinned
        : null,
    sort,
    sortIndex:
      sort && typeof column.sort_index === "number" ? column.sort_index : null
  };
}

/** A filter model with its keys in a fixed order, to compare two models. */
function filterModelKey(model: Record<string, unknown>): string {
  return JSON.stringify(
    Object.keys(model)
      .sort()
      .map((colId) => [colId, model[colId]])
  );
}

/**
 * Put a stored view on the grid: column order, visibility, pinning and sort,
 * then the filters. A column or filter the grid does not offer is not set,
 * and is named in the result. Calling it again with the same view changes
 * nothing, and fires no filter change.
 */
export function restoreGridView(
  api: GridApi,
  stored: unknown
): GridViewRestoreResult {
  const result: GridViewRestoreResult = {
    missingColumns: [],
    missingFilters: []
  };
  const view = readStoredRecord(stored);
  if (!view || api.isDestroyed()) return result;

  const columns = (Array.isArray(view.columns) ? view.columns : [])
    .map(readColumn)
    .filter((column): column is ColumnState => column !== null);
  const present = columns.filter((column) => api.getColumn(column.colId));
  result.missingColumns = columns
    .filter((column) => !api.getColumn(column.colId))
    .map((column) => column.colId);
  if (present.length > 0) {
    api.applyColumnState({
      state: present,
      applyOrder: true,
      defaultState: { sort: null }
    });
  }

  const storedFilters = readStoredRecord(view.filter_model);
  if (!storedFilters) return result;
  const filters: Record<string, unknown> = {};
  for (const [colId, model] of Object.entries(storedFilters)) {
    if (api.getColumn(colId)?.getColDef().filter) filters[colId] = model;
    else result.missingFilters.push(colId);
  }
  if (filterModelKey(api.getFilterModel() ?? {}) !== filterModelKey(filters)) {
    api.setFilterModel(filters);
  }
  return result;
}

/** The handle a keyed `DataTable` registers with the feedback integration. */
export function feedbackGridView(api: GridApi): FeedbackGridView {
  return {
    capture: () => captureGridView(api),
    restore: (stored) => restoreGridView(api, stored),
    hasRows() {
      if (api.isDestroyed()) return false;
      let hasRows = false;
      api.forEachNode(() => {
        hasRows = true;
      });
      return hasRows;
    },
    subscribe(listener) {
      function onColumns() {
        listener("columns");
      }
      function onModelUpdated() {
        listener("load");
      }
      function onColumnChange(event: { source?: string }) {
        if (USER_COLUMN_SOURCES.has(event.source ?? "")) listener("user");
      }
      function onFilterChange(event: FilterChangedEvent) {
        if (USER_FILTER_SOURCES.has(event.source ?? "")) listener("user");
      }
      const columnEvents = [
        "columnVisible",
        "columnMoved",
        "columnPinned",
        "sortChanged"
      ] as const;
      api.addEventListener("newColumnsLoaded", onColumns);
      api.addEventListener("modelUpdated", onModelUpdated);
      api.addEventListener("filterChanged", onFilterChange);
      for (const type of columnEvents) {
        api.addEventListener(type, onColumnChange);
      }
      return () => {
        if (api.isDestroyed()) return;
        api.removeEventListener("newColumnsLoaded", onColumns);
        api.removeEventListener("modelUpdated", onModelUpdated);
        api.removeEventListener("filterChanged", onFilterChange);
        for (const type of columnEvents) {
          api.removeEventListener(type, onColumnChange);
        }
      };
    }
  };
}
